/**
 * Pure commitments for [com.oasisfeng.invaluable.Link].
 *
 * These records do not register themselves into an owning graph. They are plain facts:
 * an actor-local commitment revealed a payload whose quantity becomes an issuance amount only in that actor's log.
 * Indexing and replay are explicit snapshot concerns, which keeps the arena-local Link kernel free of hidden mutation.
 *
 * ## Content off the content-addressed path (Milestone 9)
 *
 * A commitment's committed content — its [targetLink] (body) and [quantity] — does **not** sit on the chain edge.
 * The chain "spine" ([CommitmentLink]) carries only `prev -> contentDigest`, an octet leaf naming the content node's
 * projection. The content itself ([CommitmentContent] = `target + quantity`) is a separate link, referenced by digest
 * and resolved on demand. So a successor commitment attributes and orders from the spine alone, never touching any
 * ancestor's content: withholding a commitment's content is durable selective reveal — successors stay attributable
 * while the withheld one is a missing observation. Computing the digest needs a [LinkProjector] at authoring time.
 */

package com.oasisfeng.invaluable.actor

import com.oasisfeng.invaluable.Dimensions
import com.oasisfeng.invaluable.InternalTopologyApi
import com.oasisfeng.invaluable.Link
import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.NestedLinkable
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.SchemaTypes
import com.oasisfeng.invaluable.actor.CommitmentLink.Companion.actorCommitmentLogLink
import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjector
import com.oasisfeng.invaluable.data.LinkView
import com.oasisfeng.invaluable.data.Observation
import com.oasisfeng.invaluable.data.ProjectedLinkRecordResolver
import com.oasisfeng.invaluable.data.SchemaObservation
import com.oasisfeng.invaluable.data.SchemaTopology
import com.oasisfeng.invaluable.data.asOrNull
import com.oasisfeng.invaluable.data.memoizing
import com.oasisfeng.invaluable.data.observeSchema
import com.oasisfeng.invaluable.data.project
import com.oasisfeng.invaluable.data.renderProjection
import com.oasisfeng.invaluable.data.resolveLink
import com.oasisfeng.invaluable.rawTargetOrNull
import com.oasisfeng.invaluable.targetAsQuantity
import com.oasisfeng.invaluable.then
import com.oasisfeng.invaluable.toQuantityOrNull
import kotlin.jvm.JvmInline

/** Chain an [Observation] producer onto a known observation, preserving the first missing/invalid state. */
private inline fun <T, R> Observation<T>.flatMapKnown(transform: (T) -> Observation<R>): Observation<R> = when (this) {
    is Observation.Known -> transform(value)
    is Observation.Missing -> this
    is Observation.Invalid -> this
}

/**
 * Complete actor-issued commitment decoded from a [CommitmentLink].
 *
 * This is the default upper-layer view. It caches the issuer, target, quantity, and previous commitment so consumers do
 * not repeatedly walk Link structure. It is a derived view over complete topology, not an additional protocol object;
 * the durable identity remains [link] (the spine). A `Commitment` always carries *known* content; an attributed but
 * content-withheld ("dark") commitment is represented by [ObservedCommitment], not by a `Commitment` with absent fields.
 */
class Commitment internal constructor(
    val topology: CommitmentLink,
    val issuer: Actor,
    val targetLink: NestedLink,
    val quantity: Quantity,
    private val previous: Commitment?,
) : NestedLinkable {

    /** Construct the first commitment in [issuer]'s log, computing the content digest with [projector]. */
    internal constructor(issuer: Actor, targetLink: NestedLink, quantity: Quantity, projector: LinkProjector)
        : this(CommitmentLink(issuer, targetLink, quantity, projector), issuer, targetLink, quantity, previous = null)

    /** Append a commitment after [parent] in the same actor log, computing the content digest with [projector]. */
    internal constructor(parent: Commitment, targetLink: NestedLink, quantity: Quantity, projector: LinkProjector)
        : this(CommitmentLink(parent.topology, targetLink, quantity, projector), parent.issuer, targetLink, quantity, previous = parent)

    /** Durable Link identity of this commitment (the spine). */
    override val link: NestedLink get() = topology.link

    /** Ledger-ready issuance amount by the actor whose log contains this commitment. */
    data class Cost(val issuer: Actor, val amount: Quantity) {
        override fun toString(): String = "{$amount by $issuer}"
    }

    /** Previous commitment in the same actor log, or null when this is the first record or a remotely-resolved one. */
    fun previousOrNull(): Commitment? = previous

    /** Ledger-ready cost derived from [issuer] and [quantity]. */
    fun cost(): Cost = Cost(issuer, quantity)

    /**
     * All commitments from this actor log root through this commitment, in chronological order.
     *
     * Walks the in-memory [previous] chain, so it is complete for locally authored or journal-resumed heads. A commitment
     * resolved from the store ([resolve]) has no [previous] link, so its ancestry is itself alone — remote chains are
     * recovered by observing each attestation independently, not by ancestry expansion.
     */
    fun ancestry(): List<Commitment> {
        val chain = ArrayDeque<Commitment>()
        var current: Commitment? = this
        while (current != null) { chain.addFirst(current); current = current.previous }
        return chain.toList()
    }

    override fun equals(other: Any?): Boolean = other is Commitment && link == other.link
    override fun hashCode(): Int = link.hashCode()
    override fun toString(): String = link.toString()

    companion object {
        /** Return true when [node] is a complete commitment spine Link. */
        fun matchNode(node: NestedLink?): Boolean = CommitmentLink.matchNode(node)

        /**
         * Resolve a complete commitment from its head [projection] and the locally visible records.
         *
         * Known only when both the spine (for attribution) and the content node (target + quantity) are resolvable.
         * A withheld/dark content makes this [Observation.Missing] — never a placeholder.
         */
        fun resolve(projection: LinkProjection, projector: LinkProjector, records: ProjectedLinkRecordResolver): Observation<Commitment> {
            val resolver = records.memoizing()   // spine and content share an ancestry; walk it once (see [memoizing])
            return projector.resolveLink(projection, resolver).flatMapKnown { spine ->
                val cl = spine.nestedOrNull()?.let(CommitmentLink::render)
                    ?: return@flatMapKnown Observation.Invalid("Resolved head is not a commitment spine.")
                val contentProjection = cl.contentProjectionOrNull()
                    ?: return@flatMapKnown Observation.Invalid("Commitment spine has no content digest.")
                projector.resolveLink(contentProjection, resolver).mapKnownOrInvalid("Resolved commitment content is malformed.") { content ->
                    content.nestedOrNull()?.let(CommitmentContent::render)?.let { c ->
                        Commitment(cl, cl.actor(), c.targetLink(), c.quantity(), previous = null)
                    }
                }
            }
        }

        internal fun actorCommitmentLogLink(actor: Actor): NestedLink = CommitmentLink.actorCommitmentLogLink(actor)
    }
}

/**
 * Detachable committed content: `target + quantity`, the withholdable disclosure unit (Milestone 9).
 *
 * Topology (a standalone schema tuple): `DATA -> target -> quantity`, rooted at its own [schemaStructure]. A commitment
 * references this node only by the projection embedded in its spine, so the content's preimages can be withheld without
 * breaking the spine's attribution.
 */
@JvmInline value class CommitmentContent private constructor(override val link: NestedLink) : LinkView {

    companion object : SchemaTopology<CommitmentContent>(
        ctor = ::CommitmentContent, protocolIdentity = Dimensions.DATA.into(Dimensions.DATA).link,
        SchemaTypes.ANY_LINK("target"), SchemaTypes.QUANTITY("quantity"),
    ) {
        /** Build the content node committing [quantity] into [targetLink]. */
        // Parent is passed explicitly: constructTuple(NestedLink, ...) would otherwise bind [targetLink] as the parent.
        operator fun invoke(targetLink: NestedLink, quantity: Quantity): CommitmentContent =
            constructTuple(schemaStructure, targetLink, quantity)
    }

    /** Complete target Link committed to by this content. */
    fun targetLink(): NestedLink {
        val tupleData = link.target().nestedOrNull() ?: error("Invalid commitment content.")
        val parentLink = tupleData.parent()
        return parentLink.nestedOrNull()?.target()?.nestedOrNull() ?: parentLink.nestedTargetOrNull() ?: error("Invalid target link")
    }

    /** Quantity field of this content. Signed values are valid and represent negative issuance. */
    fun quantity(): Quantity = link.requireNestedTarget().targetAsQuantity() ?: error("Commitment content has no quantity.")

    override fun toString(): String = link.toString()
}

/**
 * Link identity of a complete actor-log commitment **spine**.
 *
 * Topology:
 *
 * ```text
 * actor -> CommitmentLink.schemaStructure -> contentDigest
 * previousCommitment ---------------------^
 * ```
 *
 * The first commitment in an actor log is rooted under [actorCommitmentLogLink]; later commitments use the previous
 * commitment as their parent, forming a per-actor append-only chain. The single schema field is a [contentDigest][contentProjectionOrNull]
 * — an octet leaf naming the committed [CommitmentContent]'s projection. The content (target + quantity) is *not* on this
 * Link, so resolving or attributing the spine never requires any commitment's body.
 */
@JvmInline value class CommitmentLink private constructor(override val link: NestedLink) : LinkView {

    companion object : SchemaTopology<CommitmentLink>( // Commitment spine: per-actor append-only chain of content digests
        ctor = ::CommitmentLink, protocolIdentity = Dimensions.ACTOR.into(Dimensions.DATA).link,
        SchemaTypes.ANY_LINK("content"),
    ) {
        /** Construct the first commitment in [actor]'s log; [projector] computes the content digest leaf. */
        internal operator fun invoke(actor: Actor, targetLink: NestedLink, quantity: Quantity, projector: LinkProjector) =
            constructTuple(actorCommitmentLogLink(actor), contentDigestLeaf(targetLink, quantity, projector))
        /** Append a commitment after [parent] in the same actor log; [projector] computes the content digest leaf. */
        internal operator fun invoke(parent: CommitmentLink, targetLink: NestedLink, quantity: Quantity, projector: LinkProjector) =
            constructTuple(parent.link, contentDigestLeaf(targetLink, quantity, projector))

        internal fun actorCommitmentLogLink(actor: Actor): NestedLink = actor.link.then(schemaStructure)
        override fun validateRoot(link: NestedLink): Boolean = getActorFromCommitment(link) != null

        /** The octet leaf naming the content node's projection: `LinkProjection.link()` of `project(content)`. */
        private fun contentDigestLeaf(targetLink: NestedLink, quantity: Quantity, projector: LinkProjector): NestedLink =
            projector.project(CommitmentContent(targetLink, quantity).link).link()
    }

    /** Actor whose log contains this commitment. */
    fun actor(): Actor = getActorFromCommitment(link) ?: error("Commitment Link has no actor log root.")

    /** Previous commitment in the same actor log, or null when this is the first known record. */
    fun previousLinkOrNull(): CommitmentLink? {
        val parent = link.parent().nestedOrNull() ?: error("Commitment Link has an invalid parent chain.")
        if (getActorFromCommitmentLog(parent) != null) return null
        // The root was already validated when this CommitmentLink was rendered or constructed.
        return CommitmentLink(parent)
    }

    /** Projection of the committed [CommitmentContent], decoded from the spine's content-digest leaf; null if malformed. */
    fun contentProjectionOrNull(): LinkProjection? = link.requireNestedTarget().nestedTargetOrNull()?.let(::renderProjection)

    override fun toString(): String = link.toString()
}

/** A commitment whose target was rendered into a typed complete [LinkView]. */
data class VisibleCommitment<out T : LinkView>(val commitment: Commitment, val target: T)

/**
 * Result of rendering complete commitments into a domain-specific view.
 *
 * [visible] contains commitments whose targets were understood by the supplied renderer. [unready] contains complete
 * commitments whose targets are present but outside that renderer's domain. Projection-level missing preimages belong
 * to observation APIs, not to this complete-topology rendering result.
 */
data class CommitmentRendering<out T : LinkView>(
    val visible: List<VisibleCommitment<T>>,
    val unready: List<Commitment>,
)

/**
 * Business view over a projected [Commitment] observation.
 *
 * Spine and content are observed at two levels (Milestone 9): [issuer] resolves from the spine alone (so it is known even
 * when content is withheld), while [targetLink] and [quantity] come from resolving the committed [CommitmentContent] — a
 * single disclosure unit, so they are both [Observation.Known] together or both missing/dark.
 */
class ObservedCommitment internal constructor(
    private val projector: LinkProjector,
    private val resolver: ProjectedLinkRecordResolver,
    val schema: SchemaObservation<CommitmentLink>,
) {

    /** Observe a projected commitment through the generic [SchemaTopology] tuple observer. */
    constructor(projector: LinkProjector, projection: LinkProjection, resolver: ProjectedLinkRecordResolver)
        : this(projector, resolver, projector.observeSchema(CommitmentLink, projection, resolver))

    /** Actor derived from the observed spine parent chain — known even when the committed content is withheld. */
    val issuer: Observation<Actor> = schema.parent.mapKnownOrInvalid("Commitment parent does not trace to an actor log.") {
        getActorFromCommitmentLog(it) ?: getActorFromCommitment(it)
    }

    /**
     * Projection of the committed content node, named by the spine's content digest — known even when the body is
     * withheld/dark (it is the digest leaf itself, not the resolved content). This is the key under which a sealed
     * ciphertext for this commitment is custodied (Milestone 13 / §20.2).
     */
    val contentProjection: LinkProjection? =
        (schema.fields[0].value as? Observation.Known)?.value?.nestedOrNull()?.let(::renderProjection)

    /** Resolved content node (target + quantity); [Observation.Missing] when the body is withheld/dark. */
    private val content: Observation<CommitmentContent> = schema.fields[0].value
        .mapKnownOrInvalid("Commitment content digest is not a projection.") { it.nestedOrNull()?.let(::renderProjection) }
        .flatMapKnown { contentProjection ->
            projector.resolveLink(contentProjection, resolver)
                .mapKnownOrInvalid("Resolved commitment content is malformed.") { it.nestedOrNull()?.let(CommitmentContent::render) }
        }

    /** Committed target Link, known exactly when the content is revealed. */
    val targetLink: Observation<NestedLink> = content.mapKnownOrInvalid("Commitment content has no target.") { it.targetLink() }

    /** Quantity field, known exactly when the content is revealed (bundled with the target). */
    val quantity: Observation<Quantity> = content.mapKnownOrInvalid("Commitment content has no quantity.") { it.quantity() }

    /** Full commitment identity, known only when the spine resolves and the content is revealed. */
    val complete: Observation<Commitment> = schema.complete.zipKnown(content) { cl, c ->
        Commitment(cl, cl.actor(), c.targetLink(), c.quantity(), previous = null)
    }

    /** Ledger-ready cost, known exactly when issuer and quantity are both known. */
    fun accountingCost(): Observation<Commitment.Cost> = issuer.zipKnown(quantity, Commitment::Cost)
    /** Convenience boundary for callers that only need ledger inclusion or exclusion. */
    fun accountingCostOrNull(): Commitment.Cost? = (accountingCost() as? Observation.Known)?.value
}

/**
 * Explicit immutable view over complete graph-free commitments.
 *
 * [commitments] contains only complete commitments whose content is available. Locally missing content belongs to
 * [ObservedCommitment] and other observation APIs, not to this snapshot.
 *
 * @property commitments Complete commitments in deterministic ancestry expansion order.
 */
class CommitmentSnapshot private constructor(val commitments: List<Commitment>) {

    /** Return a prefix snapshot ending at [until]. */
    fun rewind(until: Commitment): CommitmentSnapshot {
        val index = commitments.indexOf(until)
        require(index >= 0) { "Cannot rewind to a commitment outside this snapshot." }
        return CommitmentSnapshot(commitments.take(index + 1))
    }

    /** All commitments in this snapshot whose target is exactly [targetLink]. */
    fun commitmentsOf(targetLink: NestedLink): List<Commitment>
    = commitmentsByTargetLink[targetLink] ?: emptyList()

    /** Sum of all quantities issued by [issuer] in this snapshot. */
    fun totalCostBy(issuer: Actor): Quantity = commitments.fold(Quantity.ZERO) { total, commitment ->
        if (commitment.issuer == issuer) total + commitment.quantity else total
    }

    /** Actors with at least one commitment in this snapshot. */
    fun issuers(): Set<Actor> = issuerSet

    /**
     * Render this complete snapshot into a domain-specific view.
     *
     * The renderer must only return fully resolved typed [LinkView]s. A null result means the commitment is complete
     * but not understood by this domain renderer, so the commitment is carried in [CommitmentRendering.unready].
     */
    fun <T : LinkView> renderCommitments(renderTarget: (NestedLink) -> T?): CommitmentRendering<T> {
        val visible = mutableListOf<VisibleCommitment<T>>()
        val unready = mutableListOf<Commitment>()
        for (commitment in commitments) {
            val target = renderTarget(commitment.targetLink)
            if (target == null) unready += commitment else visible += VisibleCommitment(commitment, target)
        }
        return CommitmentRendering(visible = visible, unready = unready)
    }

    /**
     * Returns the complete topological closure of this snapshot.
     *
     * This self-contained set includes commitment nodes, their targets, their issuers, and all underlying [NestedLink]s
     * reachable by recursively tracing parent and target relationships. It provides the full working set of complete
     * links required to reconstruct, verify or transmit this snapshot independently without dangling references.
     */
    fun topologicalClosure(): Set<NestedLink> = topologicalClosureSet

    private val commitmentsByTargetLink: Map<NestedLink, List<Commitment>> by lazy {
        commitments.groupBy(Commitment::targetLink)
    }

    private val issuerSet: Set<Actor> by lazy {
        commitments.mapTo(mutableSetOf(), Commitment::issuer)
    }

    private val topologicalClosureSet: Set<NestedLink> by lazy {
        buildList {
            for (commitment in commitments) {
                add(commitment.link)
                add(commitment.targetLink)
                add(commitment.issuer.link)
            }
        }.reachableNestedLinks()
    }

    /** [NestedLink]s reachable through parent and target traversal from non-root heads. */
    private fun Iterable<NestedLink>.reachableNestedLinks(): Set<NestedLink> = buildSet {
        val seen = mutableSetOf<Link>()

        fun visit(link: Link) {
            if (! seen.add(link)) return
            link.nestedOrNull()?.let(::add)
            link.parentOrNull()?.let(::visit)
            @OptIn(InternalTopologyApi::class) link.rawTargetOrNull()?.let(::visit)
        }

        for (link in this@reachableNestedLinks) visit(link)
    }

    companion object {
        /** Build a snapshot from actor-log heads by expanding each head's complete ancestry. */
        operator fun invoke(heads: Iterable<Commitment>): CommitmentSnapshot {
            val commitments = linkedMapOf<NestedLink, Commitment>()
            for (head in heads) {
                for (commitment in head.ancestry()) {
                    if (commitment.link !in commitments) commitments[commitment.link] = commitment
                }
            }
            return CommitmentSnapshot(commitments.values.toList())
        }
    }
}

private fun getActorFromCommitment(link: NestedLink): Actor? {
    var current = link
    var hasRecord = false
    while (true) {
        getActorFromCommitmentLog(current)?.let { return if (hasRecord) it else null }
        // The chain of records must trace back to an actor's log root.
        hasRecord = true
        current = current.parent().nestedOrNull() ?: return null
    }
}

private fun getActorFromCommitmentLog(link: NestedLink): Actor?
= link.takeIf { it.target() == CommitmentLink.schemaStructure }?.parent()?.nestedOrNull()?.asOrNull(Actor)
